import React from 'react';
import { CountBadge } from './CountBadge';

const VARIANT_CLASSES = {
  icon: 'pill-toggle-btn',
  text: 'pill-toggle-btn-text',
  compact: 'pill-toggle-btn-compact',
};

const THEME_CLASSES = {
  admin: 'pill-toggle-btn-admin',
  employee: 'pill-toggle-btn-employee',
};

/**
 * Unified pill-toggle segmented control with mandatory theme prop.
 *
 * @param {string}   value            Currently selected option id
 * @param {function} onChange          Receives the selected option id
 * @param {Array}    options           { id, label?, icon?, title?, disabled?, count?, max? }
 * @param {'icon'|'text'|'compact'} variant  Button variant
 * @param {'admin'|'employee'}      theme    Color theme (red / blue)
 * @param {string}   [className]      Extra classes on the container div
 * @param {string}   [ariaLabel]      Accessible group label
 * @param {boolean}  [fullWidth]      Equal-width segments (always). Prefer layout="sectionHeader"
 *                                    for label+toggle rows (full on mobile, hug on desktop).
 * @param {'default'|'sectionHeader'} [layout='default']
 *   - default: natural width, or equal segments when fullWidth
 *   - sectionHeader: DS pattern — w-full equal segments on mobile; natural hug on sm+
 *     (use beside a section label; do NOT add sm:min-w-* — that leaves empty track)
 * @param {string}   [buttonClassName] Extra classes applied to every button
 * @param {number}   [countMax=99]    Default CountBadge cap when option.max is omitted
 */
export default function PillToggle({
  value,
  onChange,
  options,
  variant = 'text',
  theme = 'admin',
  className = '',
  ariaLabel,
  fullWidth = false,
  layout = 'default',
  buttonClassName = '',
  countMax = 99,
}) {
  const variantCls = VARIANT_CLASSES[variant] || VARIANT_CLASSES.text;
  const themeCls = THEME_CLASSES[theme] || THEME_CLASSES.admin;
  const isSectionHeader = layout === 'sectionHeader';

  // Equal-width modes must clip inside the track — never spill past the card edge.
  // Natural-width mode keeps overflow-x-auto from `.pill-toggle-container` for swipe scroll.
  const equalWidth = isSectionHeader || fullWidth;

  // sectionHeader bakes in the DS responsive pattern so call sites don't invent widths.
  const containerClass = [
    'pill-toggle-container',
    isSectionHeader ? 'w-full sm:w-auto flex-shrink-0' : '',
    equalWidth ? 'min-w-0' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const segmentWidthClass = isSectionHeader
    ? 'flex-1 min-w-0 overflow-hidden sm:flex-none sm:overflow-visible'
    : fullWidth
      ? 'flex-1 min-w-0 overflow-hidden'
      : 'flex-shrink-0';

  const truncateLabels = equalWidth;

  return (
    <div className={containerClass} role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const Icon = opt.icon;
        const isActive = value === opt.id;
        // Prefer explicit title; otherwise surface full label when truncated (narrow panels).
        const buttonTitle =
          opt.title ||
          (variant === 'icon' ? opt.label : undefined) ||
          (truncateLabels && opt.label ? opt.label : undefined);

        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => !opt.disabled && onChange(opt.id)}
            disabled={opt.disabled}
            className={[
              variantCls,
              themeCls,
              isActive ? 'active' : '',
              opt.disabled ? 'cursor-not-allowed opacity-50' : '',
              segmentWidthClass,
              buttonClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            title={buttonTitle}
            aria-pressed={isActive}
            aria-label={variant === 'icon' ? opt.label : undefined}
          >
            {Icon && (
              <Icon
                className={`w-4 h-4 flex-shrink-0${variant !== 'icon' && opt.label ? ' mr-1.5' : ''}`}
                aria-hidden="true"
              />
            )}
            {variant !== 'icon' && opt.label && (
              <span className={truncateLabels ? 'truncate min-w-0 flex-1' : 'whitespace-nowrap'}>
                {opt.label}
              </span>
            )}
            {opt.count != null && (
              <CountBadge
                count={opt.count}
                theme={theme}
                active={isActive}
                max={opt.max ?? countMax}
                className="ml-1.5 flex-shrink-0"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
